import { type Paginated } from "@shared/services/common";
import { AxiosError, AxiosPromise, type AxiosResponse } from "axios";
import {
  SearchResponseState,
  stateError,
  stateLoaded,
  stateLoading,
  type LoadState,
  type PaginatedLoadState,
} from "../models/state";
import { Ref } from "vue";

export async function loadClientResource<T>(
  resource: () => Promise<{ data: T; error: any }>,
  state: Ref<LoadState<T>>,
): Promise<T> {
  state.value = { ...stateLoading, result: state.value.result };
  const response = await resource().catch((e) => {
    state.value = { ...stateError, result: undefined };
    throw e;
  });
  // NOTE: the generated client does not throw on error
  if (response.error) {
    state.value = { ...stateError, result: undefined };
    throw new Error(response.error);
  }
  state.value = { ...stateLoaded, result: response.data };
  return response.data;
}

export async function loadClientPaginatedSearch<T, Q>(
  // NOTE: some endpoints use a different pagination format
  resource: (query: NoInfer<Q>) => Promise<
    | AxiosResponse<
        | Paginated<T[]>
        | {
            result: T[];
            pageSize: number;
            pagesTotal: number;
            page: number;
            total: number;
          }
      >
    | (AxiosError<any> & { data: undefined; error: any })
  >,
  state: Ref<PaginatedLoadState<T[]>>,
  query: Q,
  options?: {
    append?: boolean;
    // Background load: do not set loading state
    background?: boolean;
  },
): Promise<void> {
  if (!options?.background) {
    state.value = {
      ...stateLoading,
      result: state.value.result,
      total: state.value.total,
    };
  }
  const response = await resource(query).catch((e) => {
    state.value = { ...stateError, result: undefined };
    throw e;
  });

  if (options?.append && response.data && state.value.result) {
    state.value = {
      ...stateLoaded,
      ...response.data,
      page: {
        page: response.data.page,
        pageSize: response.data.pageSize,
      },
      result: [...state.value.result, ...response.data.result],
    };
  } else {
    state.value = {
      ...stateLoaded,
      ...response.data,
      page: {
        page: response.data?.page || 0,
        pageSize: response.data?.pageSize || 0,
      },
    };
  }
}

export async function loadResource<T>(
  resource: () => AxiosPromise<{ result: T }>,
  state: Ref<LoadState<T>>,
) {
  state.value = { ...stateLoading, result: state.value.result };
  const response = await resource().catch((e) => {
    state.value = { ...stateError, result: undefined };
    throw e;
  });
  state.value = { ...stateLoaded, result: response.data.result };
  return response;
}

export async function loadSingleResource<T>(
  resource: () => AxiosPromise<T>,
  state: Ref<LoadState<T>>,
) {
  state.value = { ...stateLoading, result: state.value.result };
  const response = await resource().catch((e) => {
    state.value = { ...stateError, result: null };
    throw e;
  });
  state.value = { ...stateLoaded, result: response.data };
  return response;
}

export async function loadPaginatedResource<T>(
  resource: () => AxiosPromise<Paginated<T[]>>,
  state: Ref<PaginatedLoadState<T[]>>,
  query?: object,
) {
  state.value = { ...state.value, ...stateLoading };
  const response = await resource().catch((e) => {
    state.value = { ...stateError, result: [] as T[], query };
    throw e;
  });
  state.value = {
    ...stateLoaded,
    ...response.data,
    page: { page: response.data.page, pageSize: response.data.pageSize },
    query,
  };
  return response;
}

export async function loadSearchResource<Result, Query>(
  resource: (q: Query) => AxiosPromise<Paginated<Result[]>>,
  state: Ref<SearchResponseState<Result[], Query>>,
  query: Query,
) {
  state.value = { ...state.value, ...stateLoading, result: state.value.result };
  const response = await resource(query).catch((e) => {
    state.value = { ...stateError, result: [] as Result[], query };
    throw e;
  });
  state.value = {
    ...stateLoaded,
    ...response.data,
    query,
  };
  return response;
}
